/**
 * Boutique TEXTIPRINT — Catalogue produits (Sprint 1 MVP).
 * Brief SIM-PLATFORM-BOUTIQUE-2026-001 §2.5 B1 + §2.3 (7 familles).
 *
 * MVP : lecture + filtres + toggle actif/featured. Création / édition complète
 * arrivera en Sprint 1.5 (dialog formulaire avancé avec upload photos Supabase
 * Storage). Pour démarrer, les 50 SKU sont seedés par
 * `scripts/seed-textprint-boutique.mjs`.
 */
import { useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, ShoppingBag, Sparkles, Tag, Layers } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import {
  useFamillesBoutique, useProduitsBoutique, useUpdateProduitBoutique,
} from '@/hooks/useBoutique';
import { UNITE_VENTE_BOUTIQUE_LABELS, type ProduitBoutique } from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';

export const BoutiqueCataloguePage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [familleFilter, setFamilleFilter]   = useState<string>('');
  const [actifFilter, setActifFilter]       = useState<'all' | 'on' | 'off'>('all');
  const [persoFilter, setPersoFilter]       = useState<'all' | 'on' | 'off'>('all');
  const [search, setSearch]                 = useState('');
  const [error, setError]                   = useState<string | null>(null);

  const { data: famillesData } = useFamillesBoutique({ limit: 100 });
  const familles = famillesData?.data ?? [];
  const familleNameById = useMemo(
    () => Object.fromEntries(familles.map((f) => [f.id, f.nom])),
    [familles],
  );

  const queryParams = useMemo(() => ({
    limit: 500,
    familleId:       familleFilter || undefined,
    actif:           actifFilter === 'all' ? undefined : actifFilter === 'on',
    personnalisable: persoFilter  === 'all' ? undefined : persoFilter  === 'on',
    search:          search.trim() || undefined,
  }), [familleFilter, actifFilter, persoFilter, search]);

  const { data, isLoading, refetch } = useProduitsBoutique(queryParams);
  const produits = data?.data ?? [];
  const update = useUpdateProduitBoutique();

  // KPIs (calculés depuis le set filtré)
  const totalActifs        = produits.filter((p) => p.actif).length;
  const totalPersonnalisab = produits.filter((p) => p.personnalisable).length;
  const totalFeatured      = produits.filter((p) => p.featured).length;
  const valeurCatalogueCents = produits.reduce(
    (s, p) => s + Number(BigInt(p.prixUnitaireHtCents ?? '0')),
    0,
  );

  const toggleActif = async (p: ProduitBoutique) => {
    setError(null);
    try { await update.mutateAsync({ id: p.id, payload: { actif: !p.actif } }); }
    catch (err) { setError(extractError(err).message); }
  };

  const toggleFeatured = async (p: ProduitBoutique) => {
    setError(null);
    try { await update.mutateAsync({ id: p.id, payload: { featured: !p.featured } }); }
    catch (err) { setError(extractError(err).message); }
  };

  return (
    <>
      <Topbar
        title="Catalogue Boutique"
        subtitle={filiale ? `${produits.length} produit(s) · ${filiale.fullName}` : 'Catalogue boutique'}
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        }
      />
      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{error}</div>
          </div>
        )}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1">
            <div className="ds-kr-ico"><ShoppingBag size={18} /></div>
            <div className="ds-kr-val">{produits.length}</div>
            <div className="ds-kr-lbl">Produits catalogue</div>
          </div>
          <div className="ds-kr kr-c2">
            <div className="ds-kr-ico"><Tag size={18} /></div>
            <div className="ds-kr-val">{totalActifs}</div>
            <div className="ds-kr-lbl">Actifs (en vente)</div>
          </div>
          <div className="ds-kr kr-c3">
            <div className="ds-kr-ico"><Sparkles size={18} /></div>
            <div className="ds-kr-val">{totalPersonnalisab}</div>
            <div className="ds-kr-lbl">Personnalisables (gadgets)</div>
          </div>
          <div className="ds-kr kr-c4">
            <div className="ds-kr-ico"><Layers size={18} /></div>
            <div className="ds-kr-val">{totalFeatured}</div>
            <div className="ds-kr-lbl">En vitrine (featured)</div>
          </div>
          <div className="ds-kr kr-c5">
            <div className="ds-kr-ico">💰</div>
            <div className="ds-kr-val">{formatNumber(valeurCatalogueCents / 100)}</div>
            <div className="ds-kr-lbl">Valeur cumulée HT (FCFA)</div>
          </div>
        </div>

        <div className="ds-filters" style={{ marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Rechercher SKU / nom / marque…"
            className="ds-fi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
          />
          <select
            className="ds-fi"
            value={familleFilter}
            onChange={(e) => setFamilleFilter(e.target.value)}
            style={{ width: 'auto' }}
          >
            <option value="">Toutes familles</option>
            {familles.map((f) => <option key={f.id} value={f.id}>{f.nom}</option>)}
          </select>
          <select
            className="ds-fi"
            value={actifFilter}
            onChange={(e) => setActifFilter(e.target.value as 'all' | 'on' | 'off')}
            style={{ width: 'auto' }}
          >
            <option value="all">Tous statuts</option>
            <option value="on">Actifs uniquement</option>
            <option value="off">Inactifs uniquement</option>
          </select>
          <select
            className="ds-fi"
            value={persoFilter}
            onChange={(e) => setPersoFilter(e.target.value as 'all' | 'on' | 'off')}
            style={{ width: 'auto' }}
          >
            <option value="all">Standard + Personnalisables</option>
            <option value="on">Personnalisables uniquement</option>
            <option value="off">Standard uniquement</option>
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Produits boutique</div>
              <div className="ds-table-sub">
                {produits.length} référence{produits.length > 1 ? 's' : ''}
                {' · '}MVP Sprint 1 — édition complète bientôt
              </div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 160 }}>SKU</th>
                <th>Nom</th>
                <th style={{ width: 100 }}>Marque</th>
                <th style={{ width: 160 }}>Famille</th>
                <th style={{ width: 110 }}>Prix HT</th>
                <th style={{ width: 80 }}>Unité</th>
                <th style={{ width: 90 }}>MOQ</th>
                <th style={{ width: 110 }}>Perso</th>
                <th style={{ width: 110 }}>Vitrine</th>
                <th style={{ width: 100 }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              ) : produits.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun produit (filtres trop stricts ? Lance le seed depuis <code>apps/api/scripts/seed-textprint-boutique.mjs</code>)</td></tr>
              ) : produits.map((p) => (
                <tr key={p.id}>
                  <td><code style={{ fontSize: 11, color: 'var(--tx-2)' }}>{p.sku}</code></td>
                  <td style={{ fontWeight: 500 }}>{p.nom}</td>
                  <td>{p.marque ?? '—'}</td>
                  <td>{familleNameById[p.familleId] ?? '—'}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMontant(p.prixUnitaireHtCents ?? '0')}
                  </td>
                  <td>{UNITE_VENTE_BOUTIQUE_LABELS[p.unite] ?? p.unite}</td>
                  <td style={{ textAlign: 'right' }}>{p.quantiteMinimum ?? '—'}</td>
                  <td>
                    {p.personnalisable ? (
                      <span className="ds-bd ds-bd-info" title={p.delaiProductionJours ? `Délai ${p.delaiProductionJours}j` : undefined}>
                        <Sparkles size={11} /> Perso
                      </span>
                    ) : <span style={{ color: 'var(--tx-3)' }}>—</span>}
                  </td>
                  <td>
                    <button
                      className={`ds-btn ds-btn-sm ${p.featured ? 'ds-btn-p' : 'ds-btn-g'}`}
                      onClick={() => void toggleFeatured(p)}
                      disabled={update.isPending}
                      title={p.featured ? 'Retirer de la vitrine' : 'Mettre en vitrine'}
                    >
                      {p.featured ? '★ Featured' : '☆ Standard'}
                    </button>
                  </td>
                  <td>
                    <button
                      className={`ds-btn ds-btn-sm ${p.actif ? 'ds-btn-s' : 'ds-btn-d'}`}
                      onClick={() => void toggleActif(p)}
                      disabled={update.isPending}
                    >
                      {p.actif ? 'Actif' : 'Inactif'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
